"use client";

import { useId } from "react";
import type { AboutDisciplineCard } from "@/lib/types";
import { BlurImage } from "@/components/ui/BlurImage";

/**
 * Expandable discipline card: image + uppercase title with black square bullet
 * and rotating "+" (CSS checkbox / peer-checked — big.dk/about pattern).
 */
export function AboutCard({ card }: { card: AboutDisciplineCard }) {
  const id = useId();

  return (
    <label
      htmlFor={id}
      data-about-card
      className="block w-[300px] min-w-0 flex-initial shrink-0 cursor-grab active:cursor-grabbing lg:w-[420px] xl:w-[500px]"
      aria-label={card.title}
    >
      <input
        type="checkbox"
        id={id}
        tabIndex={-1}
        aria-hidden
        className="peer absolute m-0 h-[25px] w-[25px] -translate-x-full opacity-0 pointer-events-none"
      />
      <BlurImage
        src={card.image.url}
        alt={card.image.alt || card.title}
        loading="lazy"
        width={card.image.width}
        height={card.image.height}
        blurDataUrl={card.image.blurDataUrl}
        wrapperClassName="max-h-[290px] lg:max-h-[310px] xl:max-h-[350px]"
        className="drag-none select-none"
        draggable={false}
      />
      <h3 className="mt-4 flex cursor-pointer items-center gap-2 text-sm uppercase select-none before:-mt-[0.5px] before:inline-block before:h-2 before:w-2 before:bg-black before:content-[''] peer-checked:[&>span]:rotate-45">
        {card.title}
        <span className="-mt-[2px] ml-1 inline-block origin-center transition-transform duration-200">
          +
        </span>
      </h3>
      <p className="mt-3 max-h-0 translate-y-[20px] overflow-hidden text-[13px] leading-4 whitespace-pre-wrap opacity-0 transition-all duration-300 peer-checked:max-h-[300px] peer-checked:translate-y-0 peer-checked:opacity-100">
        {card.body}
      </p>
    </label>
  );
}
